import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {getPagesArray, getPageStartAndEndTime as getPageStartAndEndTimeUtil, isPageVideo} from '@Libraries/page-object.library';
import {UserImageSource} from '@Libraries/user-image-library';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {UserVideoSource} from '@Libraries/user-video-library';
import type {PageObject} from '@PosterWhiteboard/page/page.types';

export const isPosterVideo = (posterObject: PosterLoadObject): boolean => {
  const pagesArray = getPagesArray(posterObject.pages.pagesHashMap, posterObject.pages.pageOrder);

  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    const times = getPageStartAndEndTimeUtil(pagesArray, pageObject.hashedID);
    if (!times) {
      return false;
    }

    if (isPageVideo(pageObject, posterObject.audioClips, times[0], times[1])) {
      return true;
    }
  }

  return false;
};

export const doesPosterMeetGettyRequirements = (posterObject: PosterLoadObject): boolean => {
  if (!hasGettyContent(posterObject)) {
    return true;
  }

  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveNonGettyContent(pageObject)) {
      return true;
    }
  }

  return false;
};

const doesPageHaveNonGettyContent = (pageObject: PageObject): boolean => {
  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype !== ITEM_TYPE.IMAGE && item.gitype !== ITEM_TYPE.VIDEO && item.gitype !== ITEM_TYPE.SLIDESHOW) {
      return true;
    }

    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if (
        imageItem.imageSource !== ImageItemSource.GETTY &&
        imageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
        imageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      ) {
        return true;
      }
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource !== UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource !== ImageItemSource.GETTY &&
            slideImageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
            slideImageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource !== UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

const doesPageHaveGettyContent = (pageObject: PageObject): boolean => {
  if (pageObject.background.details.type === BackgroundTypeName.IMAGE && pageObject.background.details.imageBackgroundItemObject?.imageSource === UserImageSource.GETTY) {
    return true;
  }

  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      return (
        imageItem.imageSource === ImageItemSource.GETTY ||
        imageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
        imageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      );
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource === UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource === ImageItemSource.GETTY ||
            slideImageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
            slideImageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource === UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

export const hasGettyContent = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveGettyContent(pageObject)) {
      return true;
    }
  }
  return false;
};
